import React, { useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { useSecurityMaster } from "../../context/SecurityMasterContext";
import { useMarketIndicators } from "../../context/MarketIndicatorContext";
import { useWatchlist } from "../../context/WatchlistContext";
import { useIndicators } from "../../context/IndicatorContext";
import { getTechnicalScores } from "../../helper/scoreHelper";
import useLivePrices from "../../hooks/useLivePrices";

// import { useStockName } from "../../context/StockNameContext";
import { useMoreStockContext } from "../../context/MoreStockContext";
import { useMarketToggle } from "../../context/MarketContext";
import { useAuth } from "../../context/AuthNewContext";
import Gauge from "../Gauge/Gauge";
import { FaSpinner } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import { useAdmin } from "../../context/AdminContext";
import { useVariant } from "../../hooks/useVariant";
import { useAIChat } from "../../context/AIChatContext";
import { FiStar } from "react-icons/fi"; // outline
import { useMarket } from "../../context/MarketDhanContext";
import { useChartIndicator } from "../../context/ChartIndicatorContext";
import { FaStar, FaRegStar } from "react-icons/fa"; // filled
import { TOOLTIPS } from "../../helper/tooltilData";

import UpgradeModal from "../UpgradeModal/UpgradeModal";
import { useStockSnapshot } from "../../context/StockSnapshotContext";
import { useIndexSnapshot } from "../../context/IndexContext";
import {
  calculateGannLevels,
  GANN_ROWS,
  getGannStatus,
  getNearestGannKey,
} from "../../helper/gannHelper";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  ComposedChart,
} from "recharts";

import { useOption } from "../../context/OptionContext";

import RangeBarSVG from "../RangeSvg/RangeBarSVG";
import ChatPanel from "../ChatPanel/ChatPanel";
import AIFloatButton from "../AIFloatButton/AIFloatButton";
import Chat from "../Chat/Chat";
import { usePdf } from "../../context/PdfContext";
import ChatPanel2 from "../ChatPanel2/ChatPanel2";
import CandleChart from "../CandleChart/CandleChart";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import { useTechnicalSnapshot } from "../../context/TechnicalSnapshotContext";
import SmartMoney from "../SmartMoney/SmartMoney";

import OverallScoreGauge from "../OverallGauge/OverallGauge";

const ENTRY_ZONE_PCT = 0.002; // 0.2% above VWAP = entry zone ceiling
const STOP_BUFFER = 0.001; // 0.1% below VWAP = stop loss buffer
const TARGET_PCT = 0.005; // 0.5% above entry = short-term target
const TARGET_PCT_PY = 0.01; // 1.0% above entry = python-style target

const runTradingAnalysis = ({
  ltp,
  vwap,
  prevClose,
  prevVWAP,
  volume,
  avgVolume,
  r1,
  s1,
  rLabel = "R1",
  sLabel = "S1",
  position,
}) => {
  if (!ltp || !vwap) return {};

  const isAbove = ltp > vwap;
  const isBelow = ltp < vwap;
  const diff = ltp - vwap;
  const inPosition = !!position?.inPosition;
  const entryPrice = position?.entryPrice ?? null;

  // ── DERIVED VALUES (always computed) ──
  const stopLoss = isAbove
    ? (vwap * (1 - STOP_BUFFER)).toFixed(2)
    : (vwap * (1 + STOP_BUFFER)).toFixed(2);

  const targets = {
    primary: {
      label: isAbove ? rLabel || "R1" : sLabel || "S1",
      value: isAbove ? (r1 ?? null) : (s1 ?? null),
    },
  };

  // ── NOT IN POSITION → ENTRY LOGIC ──
  if (!inPosition) {
    const { entry, signal, newPosition, pythonSignal, pythonTrend } =
      resolveEntry(ltp, vwap, prevClose, prevVWAP, volume, avgVolume, position);

    const traderNote = pythonSignal || `WAIT → VWAP ₹${vwap.toFixed(2)}`;

    return {
      entry,
      signal,
      exit: null, // no exit when not in a trade
      targets,
      stopLoss,
      traderNote,
      newPosition,
      pythonSignal,
      pythonTrend,
    };
  }

  // ── IN POSITION → EXIT LOGIC ──
  const { exit, newPosition, pythonSignal, pythonTrend } = resolveExit(
    ltp,
    vwap,
    entryPrice,
    position,
  );

  const traderNote = `IN TRADE → Entry ₹${entryPrice} | Target ₹${(
    entryPrice *
    (1 + TARGET_PCT_PY)
  ).toFixed(2)}`;

  return {
    entry: {
      label: "In Trade",
      msg: `Entry ₹${entryPrice} | LTP ₹${ltp}`,
      type: "active",
    },
    signal: "IN TRADE",
    exit,
    targets,
    stopLoss,
    traderNote,
    newPosition,
    pythonSignal,
    pythonTrend,
  };
};

// ═══════════════════════════════════
// ENTRY RESOLVER (not in position)
// ═══════════════════════════════════
function resolveEntry(
  ltp,
  vwap,
  prevClose,
  prevVWAP,
  volume,
  avgVolume,
  position,
) {
  const ceiling = vwap * (1 + ENTRY_ZONE_PCT);
  const isInZone = ltp >= vwap && ltp <= ceiling;
  const isReclaim = prevClose < (prevVWAP || vwap) && ltp > vwap;
  const hasVolume = !volume || !avgVolume || volume > avgVolume * 0.8;

  const makeEntry = (label, msg, type) => ({ label, msg, type });
  const openPosition = () => ({
    inPosition: true,
    entryPrice: Number(ltp.toFixed(2)),
  });

  // Priority 1: VWAP Reclaim (strongest)
  if (isReclaim && hasVolume) {
    return {
      entry: makeEntry("VWAP Reclaim Buy", "Strong breakout above VWAP", "buy"),
      signal: `Bullish (Reclaim) @ ₹${ltp}`,
      newPosition: openPosition(),
      pythonSignal: `BUY at ₹${ltp}, VWAP ₹${vwap.toFixed(2)}`,
      pythonTrend: "BULLISH ENTRY",
    };
  }

  // Priority 2: Pullback Buy (in entry zone)
  if (isInZone) {
    return {
      entry: makeEntry("Pullback Buy", "VWAP Support Zone", "buy"),
      signal: `Bullish (Pullback) @ ₹${ltp}`,
      newPosition: openPosition(),
      pythonSignal: `BUY at ₹${ltp}, VWAP ₹${vwap.toFixed(2)}`,
      pythonTrend: "BULLISH ENTRY",
    };
  }

  // Priority 3: Above VWAP (fallback bullish)
  if (ltp > vwap) {
    return {
      entry: makeEntry("Fallback Bullish", "Above VWAP (fallback)", "buy"),
      signal: `Bullish (Fallback) @ ₹${ltp}`,
      newPosition: position,
      pythonSignal: `LTP ₹${ltp} is above VWAP`,
      pythonTrend: "TRENDING BULLISH",
    };
  }

  // Priority 4: Below VWAP (fallback bearish)
  if (ltp < vwap) {
    return {
      entry: makeEntry("Fallback Bearish", "Below VWAP (fallback)", "sell"),
      signal: `Bearish (Fallback) @ ₹${ltp}`,
      newPosition: position,
      pythonSignal: `LTP ₹${ltp} is below VWAP`,
      pythonTrend: "TRENDING BEARISH",
    };
  }

  // Priority 5: Exactly at VWAP (rare edge case)
  return {
    entry: makeEntry("Neutral", "Consolidating at VWAP", "neutral"),
    signal: `Neutral @ ₹${ltp}`,
    newPosition: position,
    pythonSignal: `LTP ₹${ltp} at VWAP ₹${vwap.toFixed(2)}`,
    pythonTrend: "NEUTRAL",
  };
}

// ═══════════════════════════════════
// EXIT RESOLVER (in position)
// ═══════════════════════════════════
function resolveExit(ltp, vwap, entryPrice, position) {
  const target = entryPrice * (1 + TARGET_PCT);
  const stopLoss = vwap * (1 - STOP_BUFFER);
  const closePosition = () => ({ inPosition: false, entryPrice: null });

  // Priority 1: Target hit
  if (ltp >= target) {
    return {
      exit: `Target Hit 🚀 SELL @ ₹${ltp}`,
      newPosition: closePosition(),
      pythonSignal: `SELL at ₹${ltp}, Gain: ${(((ltp - entryPrice) / entryPrice) * 100).toFixed(2)}%`,
      pythonTrend: "EXIT PROFIT",
    };
  }

  // Priority 2: VWAP breakdown
  if (ltp < stopLoss) {
    return {
      exit: `VWAP Breakdown ❌ SELL @ ₹${ltp}`,
      newPosition: closePosition(),
      pythonSignal: `LOSS at ₹${ltp}, Below VWAP`,
      pythonTrend: "EXIT STOP",
    };
  }

  // Priority 3: Hold
  return {
    exit: `HOLD → LTP ₹${ltp}`,
    newPosition: position,
    pythonSignal: `HOLD at ₹${ltp}, Entry ₹${entryPrice}`,
    pythonTrend: "HOLDING",
  };
}

// const IndexDetail = () => {
//   const {
//     indexDetail,
//     indexDetailLoading,
//     indexDetailError,
//     getIndexSnapshot,
//   } = useIndexSnapshot();

//   const { index_name } = useParams();
//   console.log("IndexName", index_name);

//   useEffect(() => {
//     getIndexSnapshot(index_name);
//   }, [index_name]);

//    const quote = indexDetail?.quote?.data;
//   const intraday = indexDetail?.intraday;
//   const chartIndicator = indexDetail?.chart_indicator;
//   const signals = indexDetail?.signals;
//   const histData = indexDetail?.hist_data;

//   const ltp = quote?.last_price;
//   const ohlc = quote?.ohlc || {};

//   const open = ohlc?.open;
//   const high = ohlc?.high;
//   const low = ohlc?.low;
//   const prevClose = ohlc?.close;

//   const percentChange =
//     ltp && prevClose
//       ? Number((((ltp - prevClose) / prevClose) * 100).toFixed(2))
//       : 0;

//   const priceDiff =
//     ltp && prevClose ? Number((ltp - prevClose).toFixed(2)) : 0;

//   const isPositive = percentChange >= 0;
//   console.log("IndexDetail", indexDetail);
//    const handleRefresh = () => {
//     if (!index_name) return;
//     getIndexSnapshot(index_name);
//   };
//     const formatTimestamp = (dateValue) => {
//     if (!dateValue) return "";

//     const now = new Date(dateValue);
//     if (isNaN(now)) return "";

//     return now.toLocaleString("en-IN", {
//       timeZone: "Asia/Kolkata",
//       day: "2-digit",
//       month: "short",
//       year: "numeric",
//       hour: "2-digit",
//       minute: "2-digit",
//       second: "2-digit",
//     });
//   };

//   return (
//     <>

//     </>
//   )
// };

// export default IndexDetail;

const IndexDetail = () => {
  // const {
  //   marketSnapshot,
  //   marketLoading,
  //   marketError,
  //   getMarketSnapshot,
  //   resetMarketSnapshot,
  //   initializeMarketSnapshot,
  // } = useMarketSnapshot();
  const {
    stockDetail,
    stockDetailLoading,
    stockDetailError,
    getStockDetailSnapshot,
    resetStockDetail,
    getStockFundamentals,
    fundamentals,
    fundamentalsError,
    fundamentalsLoading,
  } = useStockSnapshot();
  const { downloadStockPDF } = usePdf();
  // const { fetchBySymbol, pcr } = useOption();
  // const {
  //   getTechnicalSnapshot,
  //   technicalSnapshot,
  //   resetTechnicalSnapshot,
  //   initializeTechnicalSnapshot,
  // } = useTechnicalSnapshot();
  const [crossovers, setCrossovers] = useState([]);
  const [liveReady, setLiveReady] = useState(false);
  const { market } = useMarketToggle();
  const [maxChart, setMaxChart] = useState(null);
  const [timeFrame, setTimeFrame] = useState("min");
  const [resistTimeFrame, setResistTimeFrame] = useState("day");
  const [position, setPosition] = useState({
    inPosition: false,
    entryPrice: null,
  });
  const [smartMode, setSmartMode] = useState("intraday");
  const {
    indexDetail,
    indexDetailLoading,
    indexDetailError,
    getIndexSnapshot,
    resetIndexSnapshot,
  } = useIndexSnapshot();
  const [lastUTSignal, setLastUTSignal] = useState(null);
  const [interval, setInterval] = useState("1d");
  // const {
  //   getTargetPrice,
  //   targetPrice,
  //   getNameBySymbol,
  //   stockName,
  //   resetTargetPrice,
  // } = useMarketIndicators();
  const navigate = useNavigate();
  const {
    addToWatchlist,
    removeFromWatchlist,
    resetWatchlist,
    fetchWatchlist,
    watchlist,
    isInWatchlist,
  } = useWatchlist();
  const { prices: livePrices, subscribeSymbols } = useLivePrices();
  const variant = useVariant();
  const { chatOpen } = useAIChat();
  const { symbol, securityId: paramSecurityId } = useParams();
  const [resData, setResData] = useState("daily");
  const [visibleCount, setVisibleCount] = useState(8);

  console.count("StockTest Render");
  const { index_name } = useParams();
  useEffect(() => {
    if (!index_name) return;
    getIndexSnapshot(index_name);
    subscribeSymbols([index_name]);
  }, [index_name]);

  //   useEffect(() => {
  //   getIndexSnapshot(index_name);
  // }, [index_name]);

  // ━━━ NEW: live price via websocket (same as IndexTabs) ━━━

  // useEffect(() => {
  //   if (!index_name) return;
  //     // e.g. ["NIFTY 50"]
  // }, [index_name]);

  const live = livePrices?.[index_name];

  //   console.log("Stock Snapshot", stockDetail);
  const marketSnapshot = stockDetail?.marketSnapshot;
  const technicalSnapshot = stockDetail?.technicalSnapshot;
  const stockName = stockDetail?.stockData;
  const targetPrice = fundamentals?.targetPrice;
  //   console.log("Fundamentals", fundamentals);
  const quote = indexDetail?.quote?.data;
  const intraday = indexDetail?.intraday;
  const chartIndicator = indexDetail?.chart_indicator;
  const signals = indexDetail?.signals;
  const histData = indexDetail?.hist_data;

  const ltp = live?.ltp || quote?.last_price;
  const ohlc = quote?.ohlc || {};

  const open = ohlc?.open;
  const high = ohlc?.high;
  const low = ohlc?.low;
  //   const prevClose = ohlc?.close;
  // const prevClose = indexDetail?.hist_data?.hist?.close?.at(-1);
  const prevClose =
    indexDetail?.prev_close ?? indexDetail?.hist_data?.hist?.close?.at(-1);

  const gannLevels =
    indexDetail?.gann_levels ??
    calculateGannLevels(indexDetail?.prev_close ?? ohlc?.close); // from backend
  const gannLtp = Number(ltp); // your live ltp
  const gannNearestKey = getNearestGannKey(gannLevels, gannLtp);
  const percentChange =
    ltp && prevClose
      ? Number((((ltp - prevClose) / prevClose) * 100).toFixed(2))
      : 0;

  const priceDiff = ltp && prevClose ? Number((ltp - prevClose).toFixed(2)) : 0;

  const isPositive = percentChange >= 0;
  console.log("IndexDetail", indexDetail);
  const handleRefresh = () => {
    if (!index_name) return;
    getIndexSnapshot(index_name);
  };

  //   console.log("Market Snapshot", marketSnapshot);
  //   console.log("Technical Snapshot", technicalSnapshot);
  //   console.log("Target Price", targetPrice);
  // console.log("StockName ", stockName);

  const vwap = intraday?.vwap;
  //   console.log("toye of vwap", typeof vwap);
  const bb_signal = intraday?.bbawe_signal;
  const ut_signal = intraday?.ut_signal;
  console.log("signals", indexDetail?.signals);
  //   console.log("ut_signal", ut_signal);
  const vwap_signal = intraday?.vwap_signal;
  const rsi_macd_signal = intraday?.prediction;
  const diff = vwap && ltp ? ltp - vwap : 0;
  const percent = vwap ? ((diff / vwap) * 100).toFixed(2) : 0;
  // const rawPrice = targetPrice?.data?.target_price;
  // const target_price = rawPrice
  //   ? Number(String(rawPrice).replace(/,/g, ""))
  //   : 0;

  // const rawLowPrice = targetPrice?.data?.target_low;

  // ✅ only use targetPrice if it matches current symbol

  const rawPrice =
    targetPrice?.symbol === symbol ? targetPrice?.target_price : null;
  const target_price = rawPrice
    ? Number(String(rawPrice).replace(/,/g, ""))
    : 0;

  const rawLowPrice =
    targetPrice?.symbol === symbol ? targetPrice?.target_low : null;

  const rawHighPrice =
    targetPrice?.symbol === symbol ? targetPrice?.target_high : null;
  const target_low = rawLowPrice
    ? Number(String(rawLowPrice).replace(/,/g, ""))
    : 0;

  // const rawHighPrice = targetPrice?.data?.target_high;
  const target_high = rawHighPrice
    ? Number(String(rawHighPrice).replace(/,/g, ""))
    : 0;
  console.log("Target Price", target_price);

  const projectChange =
    target_price &&
    (((target_price - quote?.last_price) / quote?.last_price) * 100).toFixed(2);
  console.log("ProjectedChange", projectChange);
  const maData = indexDetail?.hist_data?.ma_data?.movingAverages || [];

  // resDaily
  const resDaily = indexDetail?.resDaily;
  //resWeekly
  const resWeekly = indexDetail?.resWeekly;
  const resistanceDataToRender =
    resistTimeFrame === "day" ? resDaily : resWeekly;
  //   const ltp = quote?.last_price;

  const { nearestResistance, nearestSupport, pivotLevels } = useMemo(() => {
    const pivotLevels = resistanceDataToRender?.classic;
    if (!pivotLevels || !ltp) {
      return {
        nearestResistance: null,
        nearestSupport: null,
        pivotLevels: null,
      };
    }

    const resistanceLevels = [
      pivotLevels.R1,
      pivotLevels.R2,
      pivotLevels.R3,
    ].filter((v) => typeof v === "number" && v > ltp);

    const supportLevels = [
      pivotLevels.S1,
      pivotLevels.S2,
      pivotLevels.S3,
    ].filter((v) => typeof v === "number" && v < ltp);

    return {
      pivotLevels,
      nearestResistance: resistanceLevels.length
        ? { value: Math.min(...resistanceLevels) }
        : null,
      nearestSupport: supportLevels.length
        ? { value: Math.max(...supportLevels) }
        : null,
    };
  }, [resistanceDataToRender, ltp]);
  const latestVWAP = Number(vwap);
  const ltpValue = Number(ltp);

  //   const prevClose = technicalSnapshot?.ohcl?.close;

  // Remove the position sync useEffect entirely and handle it inside useMemo
  const analysis = useMemo(() => {
    if (!quote || !latestVWAP) return null;

    const result = runTradingAnalysis({
      ltp: quote?.last_price,
      vwap: latestVWAP,
      prevClose: prevClose || quote?.last_price,
      prevVWAP: latestVWAP,
      volume: quote?.volume ?? 0,
      avgVolume: 0,
      r1: nearestResistance?.value,
      s1: nearestSupport?.value,
      rLabel: nearestResistance?.key,
      sLabel: nearestSupport?.key,
      position,
    });

    return result;
  }, [quote?.last_price, latestVWAP, nearestResistance, nearestSupport]);
  // 🔥 REMOVED position from deps — breaks the loop

  // Replace the sync useEffect with a one-time position setter
  useEffect(() => {
    if (!analysis?.newPosition) return;
    // Only update position on initial load or when price actually changes
    setPosition((prev) => {
      if (
        prev.inPosition === analysis.newPosition.inPosition &&
        prev.entryPrice === analysis.newPosition.entryPrice
      ) {
        return prev; // same — no re-render
      }
      return analysis.newPosition;
    });
  }, [quote?.last_price]); // ← only when price changes, not on every analysis

  useEffect(() => {
    if (!analysis?.newPosition) return;

    const prev = position || {};
    const next = analysis.newPosition || {};

    const isSame =
      prev.inPosition === next.inPosition &&
      Number(prev.entryPrice || 0).toFixed(2) ===
        Number(next.entryPrice || 0).toFixed(2);

    if (!isSame) {
      setPosition(next);
    }
  }, [analysis?.newPosition]);
  const getSignalTooltip = (signal) => {
    if (!signal) return [];

    if (signal.includes("Fallback")) {
      return [
        { id: 1, text: "Fallback Signal" },
        { id: 2, text: "Weak direction based on VWAP" },
        { id: 3, text: "No strong confirmation present" },
        { id: 4, text: "Use with caution" },
      ];
    }

    if (signal.includes("Reclaim")) {
      return [
        { id: 1, text: "VWAP Reclaim" },
        { id: 2, text: "Strong bullish signal" },
        { id: 3, text: "Price crossed above VWAP" },
      ];
    }

    if (signal.includes("Pullback")) {
      return [
        { id: 1, text: "VWAP Pullback" },
        { id: 2, text: "Medium strength signal" },
        { id: 3, text: "Price bouncing near VWAP" },
      ];
    }

    return [
      { id: 1, text: "Trading Signal" },
      { id: 2, text: "Based on strategy conditions" },
    ];
  };
  const InfoTooltip = ({ text }) => {
    if (!Array.isArray(text)) return null;

    return (
      <span className="table-tooltip-wrapper">
        <span style={{ color: "black" }}> ⓘ</span>
        <div className="table-tooltip-content">
          {text.map((item) => (
            <div key={item.id} className="tooltip-line">
              {item.text}
            </div>
          ))}
        </div>
      </span>
    );
  };
  // const InfoTooltip = ({ text }) => {
  //   if (!Array.isArray(text)) return null;

  //   return (
  //     <span className="table-tooltip-wrapper">
  //       <span className="tooltip-icon">ⓘ</span>

  //       <span className="table-tooltip-content">
  //         {text.map((item) => (
  //           <span key={item.id} className="tooltip-line">
  //             {item.text}
  //           </span>
  //         ))}
  //       </span>
  //     </span>
  //   );
  // };

  const levels = [
    { key: "R3", label: "Third Resistance" },
    { key: "R2", label: "Second Resistance" },
    { key: "R1", label: "First Resistance" },
    { key: "P", label: "Pivot" },
    { key: "S1", label: "First Support" },
    { key: "S2", label: "Second Support" },
    { key: "S3", label: "Third Support" },
  ];

  const getStatus = (value, ltp) => {
    if (!value || !ltp) return "—";

    if (value > ltp) return "Above LTP";
    if (value < ltp) return "Below LTP";

    return "At LTP";
  };

  const getPriority = (value, ltp) => {
    if (!value || !ltp) return 0;

    const diffPercent = Math.abs((value - ltp) / ltp) * 100;

    if (diffPercent <= 0.5) return 3;
    if (diffPercent <= 1.5) return 2;
    if (diffPercent <= 3) return 1;

    return 0;
  };

  const renderStars = (priority) => {
    if (priority === 3) return "⭐⭐⭐";
    if (priority === 2) return "⭐⭐";
    if (priority === 1) return "⭐";
    return "—";
  };

  const weekLow = quote?.["52_week_low"];
  const weekHigh = quote?.["52_week_high"];

  const buildMARows = (ltp, maData = []) => {
    if (!ltp || !Array.isArray(maData)) return [];

    return maData.map((ma) => {
      const smaTrend = ltp >= ma?.sma ? "Buy" : "Sell";
      const emaTrend = ltp >= ma?.ema ? "Buy" : "Sell";

      return {
        name: ma?.name || "-",
        smaValue: Number(ma?.sma || 0),
        emaValue: Number(ma?.ema || 0),
        smaTrend,
        emaTrend,
      };
    });
  };

  const buildMASummary = (rows = []) => {
    const finalBuy =
      rows.filter((r) => r.smaTrend === "Buy").length +
      rows.filter((r) => r.emaTrend === "Buy").length;

    const finalSell =
      rows.filter((r) => r.smaTrend === "Sell").length +
      rows.filter((r) => r.emaTrend === "Sell").length;

    return { finalBuy, finalSell };
  };
  const rows = useMemo(() => {
    if (!ltp || !maData?.length) return [];
    return buildMARows(ltp, maData);
  }, [ltp, maData]);

  // const summary = buildMASummary(rows);
  const summary = useMemo(() => buildMASummary(rows), [rows]);
  //ma data moving averages

  // depth from quotes

  const depth = quote?.depth;
  console.log("Depth from quote", depth);
  const buyQty = depth?.buy?.reduce((s, b) => s + b.quantity, 0);
  const sellQty = depth?.sell?.reduce((s, s1) => s + s1.quantity, 0);
  const buyOrders = depth?.buy?.reduce((s, b) => s + b.orders, 0);
  const sellOrders = depth?.sell?.reduce((s, s1) => s + s1.orders, 0);
  const totalBuyQty = depth?.buy?.reduce(
    (sum, b) => sum + (b.quantity || 0),
    0,
  );

  const totalSellQty = depth?.sell?.reduce(
    (sum, s) => sum + (s.quantity || 0),
    0,
  );

  const totalQty = totalBuyQty + totalSellQty;

  const buyPercent = totalQty ? (totalBuyQty / totalQty) * 100 : 0;

  const sellPercent = totalQty ? (totalSellQty / totalQty) * 100 : 0;

  //crossover

  const detectCrossovers = (data, priceData) => {
    const signals = [];

    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1];
      const curr = data[i];

      if (
        prev.MACD == null ||
        prev.signal == null ||
        curr.MACD == null ||
        curr.signal == null
      )
        continue;

      const price = priceData?.[i]?.close;

      // BUY crossover
      if (prev.MACD < prev.signal && curr.MACD > curr.signal) {
        signals.push({
          type: "BUY",
          date: curr.date,
          index: i,
          price,
          macd: curr.MACD,
          signal: curr.signal,
        });
      }

      // SELL crossover
      if (prev.MACD > prev.signal && curr.MACD < curr.signal) {
        signals.push({
          type: "SELL",
          date: curr.date,
          index: i,
          price,
          macd: curr.MACD,
          signal: curr.signal,
        });
      }
    }

    return signals;
  };

  const calculateTradePerformance = (signals, priceData) => {
    const trades = [];

    let activeTrade = null;

    for (let i = 0; i < signals.length; i++) {
      const signal = signals[i];

      // =========================
      // BUY ENTRY
      // =========================
      if (signal.type === "BUY" && !activeTrade) {
        activeTrade = {
          entryDate: signal.date,
          entryPrice: signal.price,

          highestPrice: signal.price,

          status: "OPEN",
        };
      }

      // =========================
      // TRACK MAX PROFIT
      // =========================
      if (activeTrade) {
        const currentPrice = signal.price;

        if (currentPrice > activeTrade.highestPrice) {
          activeTrade.highestPrice = currentPrice;
        }
      }

      // =========================
      // SELL EXIT
      // =========================
      if (signal.type === "SELL" && activeTrade) {
        const exitPrice = signal.price;

        const pnl =
          ((exitPrice - activeTrade.entryPrice) / activeTrade.entryPrice) * 100;

        const maxProfit =
          ((activeTrade.highestPrice - activeTrade.entryPrice) /
            activeTrade.entryPrice) *
          100;

        trades.push({
          ...activeTrade,

          exitDate: signal.date,
          exitPrice,

          pnl: pnl.toFixed(2),
          maxProfit: maxProfit.toFixed(2),

          status: "CLOSED",
        });

        activeTrade = null;
      }
    }

    return trades;
  };

  const intradayData = indexDetail?.intraday;
  const indicatorData = indexDetail?.indicator_data;
  const indicatorsMin = intradayData?.indicators;
  const masterTimelineMin = intradayData?.closeSeries || [];

  const macdChartDataMin = useMemo(() => {
    if (!masterTimelineMin?.length || !indicatorsMin?.macd?.length) return [];

    const offset = masterTimelineMin.length - indicatorsMin.macd.length;

    return masterTimelineMin.map((p, i) => {
      const macdIndex = i - offset;

      if (macdIndex < 0) {
        return { date: p.date, MACD: null, signal: null, histogram: null };
      }

      const m = indicatorsMin.macd[macdIndex];
      return {
        date: p.date,
        MACD: m.macd,
        signal: m.signal,
        histogram: m.histogram,
      };
    });
  }, [masterTimelineMin, indicatorsMin?.macd]);
  const minPriceBase = intradayData?.closeSeries || [];
  const dayPriceBase = indexDetail?.hist_data?.closeSeries || [];
  const macdChartData = useMemo(() => {
    const macdArr = indexDetail?.hist_data?.indicators?.macd || [];
    const priceArr = indexDetail?.hist_data?.closeSeries || [];

    return priceArr.map((p) => {
      const m = macdArr.find((item) => item.date === p.date);

      return {
        date: p.date,
        MACD: m?.macd ?? null,
        signal: m?.signal ?? null,
        histogram: m?.histogram ?? null,
      };
    });
  }, [indexDetail]);
  console.log("Macd chart data ", macdChartData);

  //   const newsData = useMemo(() => {
  //     return fundamentals?.news;
  //   }, [fundamentals]);
  useEffect(() => {
    if (!macdChartDataMin?.length || !minPriceBase?.length) return;

    const signals = detectCrossovers(macdChartDataMin, minPriceBase);

    // setCrossovers((prev) => {
    //   if (JSON.stringify(prev) === JSON.stringify(signals)) {
    //     return prev;
    //   }

    //   return signals;
    // });
    setCrossovers((prev) => {
      if (
        prev.length === signals.length &&
        prev[prev.length - 1]?.date === signals[signals.length - 1]?.date
      ) {
        return prev;
      }
      return signals;
    });
  }, [macdChartDataMin, minPriceBase]);

  //min dropdown for chart
  const resampleMinuteMACD = (data, step) => {
    if (!Array.isArray(data) || step === 1) return data;

    const result = [];

    for (let i = 0; i < data.length; i += step) {
      const chunk = data.slice(i, i + step);
      if (chunk.length < step) continue;

      const last = chunk[chunk.length - 1];

      result.push({
        date: last.date, // last candle time
        MACD: last.MACD,
        signal: last.signal,
        histogram: chunk.reduce((sum, c) => sum + (c.histogram ?? 0), 0),
      });
    }

    return result;
  };

  const resampleMinutePrice = (data, step) => {
    if (!Array.isArray(data) || step === 1) return data;

    const result = [];
    for (let i = 0; i < data.length; i += step) {
      const chunk = data.slice(i, i + step);
      if (chunk.length < step) continue;

      const last = chunk[chunk.length - 1];
      result.push({
        date: last.date,
        close: last.close,
      });
    }
    return result;
  };

  const resampleDailyPrice = (data, interval) => {
    if (!Array.isArray(data) || interval === "1d") return data;

    const groups = new Map();

    data.forEach((row) => {
      const d = new Date(row.date);
      if (isNaN(d.getTime())) return;

      let key;

      if (interval === "1w") {
        const year = d.getFullYear();
        const week = Math.ceil(
          ((d - new Date(year, 0, 1)) / 86400000 +
            new Date(year, 0, 1).getDay() +
            1) /
            7,
        );
        key = `${year}-W${week}`;
      }

      if (interval === "1mo") {
        key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      }

      if (interval === "3mo") {
        const q = Math.floor(d.getMonth() / 3) + 1;
        key = `${d.getFullYear()}-Q${q}`;
      }

      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });

    return Array.from(groups.values()).map((group) => {
      const last = group[group.length - 1];
      return {
        date: last.date,
        close: last.close,
      };
    });
  };
  const selectedPriceData = useMemo(() => {
    if (timeFrame === "min") {
      const stepMap = { "1m": 1, "3m": 3, "5m": 5 };
      return resampleMinutePrice(minPriceBase, stepMap[interval] || 1);
    }

    return resampleDailyPrice(dayPriceBase, interval);
  }, [timeFrame, interval, minPriceBase, dayPriceBase]);

  //day dropdown
  const resampleDailyMACD = (data, interval) => {
    if (!Array.isArray(data) || interval === "1d") return data;

    const groups = new Map();

    data.forEach((row) => {
      const d = new Date(row.date);
      if (isNaN(d.getTime())) return;

      let key;

      // 📅 WEEKLY
      if (interval === "1w") {
        const year = d.getFullYear();
        const week = Math.ceil(
          ((d - new Date(year, 0, 1)) / 86400000 +
            new Date(year, 0, 1).getDay() +
            1) /
            7,
        );
        key = `${year}-W${week}`;
      }

      // 📅 MONTHLY
      if (interval === "1mo") {
        key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      }

      // 📅 3 MONTHS
      if (interval === "3mo") {
        const quarter = Math.floor(d.getMonth() / 3) + 1;
        key = `${d.getFullYear()}-Q${quarter}`;
      }

      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });

    return Array.from(groups.values()).map((group) => {
      const last = group[group.length - 1];

      return {
        date: last.date, // last trading day
        MACD: last.MACD,
        signal: last.signal,
        histogram: group.reduce((sum, g) => sum + (g.histogram ?? 0), 0),
      };
    });
  };
  //chart
  const selectedData = (() => {
    // ⏱ INTRADAY
    if (timeFrame === "min") {
      if (!Array.isArray(macdChartDataMin)) return [];

      if (interval === "1m") return macdChartDataMin;
      if (interval === "3m") return resampleMinuteMACD(macdChartDataMin, 3);
      if (interval === "5m") return resampleMinuteMACD(macdChartDataMin, 5);

      return macdChartDataMin; // safe fallback
    }

    // 📅 DAILY / HIGHER
    if (!Array.isArray(macdChartData)) return [];

    if (interval === "1d") return macdChartData;
    if (interval === "1w") return resampleDailyMACD(macdChartData, "1w");
    if (interval === "1mo") return resampleDailyMACD(macdChartData, "1mo");
    if (interval === "3mo") return resampleDailyMACD(macdChartData, "3mo");

    return macdChartData; // safe fallback
  })();
  const intradayVwap = intradayData;

  //error handling
  // const isFetchingLiveData =
  //   marketLoading || !marketSnapshot || !technicalSnapshot || !quote;
  // const isFetchingLiveData =
  //   !liveReady ||
  //   marketLoading ||
  //   !marketSnapshot ||
  //   !technicalSnapshot ||
  //   !quote;
  const isFetchingLiveData = !liveReady || stockDetailLoading;
  const returnData = indexDetail?.return_data?.returns;
  //return data
  const oneWeekReturn = returnData?.oneWeek;

  const oneMonthReturn = returnData?.oneMonth;
  const ytdReturn = returnData?.ytd;
  const oneYearReturn = returnData?.oneYear;

  const threeYearReturn = returnData?.threeYear;
  const fiveYearReturn = returnData?.fiveYear;
  const getBgColor = (text) => {
    if (text === "BUY" || text === "Bullish") {
      return "#249966";
    } else if (text === "SELL" || text === "Bearish") {
      return "#dc2626";
    } else {
      return "#fbbf24";
    }
  };
  const day_pred = technicalSnapshot?.prediction;
  const min_pred = rsi_macd_signal;
  const day_result = technicalSnapshot?.prediction_result;
  const min_result = intraday?.prediction_result;
  const chartIndicatorData = intraday?.chart_indicator;
  const formatTimestamp = (dateValue) => {
    if (!dateValue) return "";

    const now = new Date(dateValue);
    if (isNaN(now)) return "";

    const day = String(now.getDate()).padStart(2, "0");

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();

    const time = now.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    return `${day}-${month}-${year} | ${time}`;
  };

  const formattedQuoteTime = formatTimestamp(marketSnapshot?.quote_updated_at);
  const formattedIntradayTime = formatTimestamp(
    marketSnapshot?.intraday_updated_at,
  );

  const formatVolume = (volume) => {
    if (volume == null) return "-"; // catches null & undefined

    const num = Number(volume);
    if (!Number.isFinite(num)) return "-";

    return num.toLocaleString("en-IN");
  };
  //   const handleRefresh = async () => {
  //     try {
  //       if (!symbol) return;

  //       await getStockDetailSnapshot(symbol, paramSecurityId);
  //     } catch (error) {
  //       console.error("Stock detail refresh failed", error);
  //     }
  //   };

  const statisticsData = useMemo(() => {
    return fundamentals?.keyStatistics?.body;
  }, [fundamentals]);
  // const peRatio = (useMemo) => (
  //   () => {
  //     return statisticsData?.forwardPE.fmt;
  //   },
  //   [statisticsData]
  // );
  const peRatio = useMemo(
    () => statisticsData?.forwardPE?.fmt ?? "—",
    [statisticsData],
  );

  //donwload report btn

  const safeNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(num) ? num : null;
  };

  const buildPDFPayload = () => {
    const quote = marketSnapshot?.quote?.data;
    const ohlc = quote?.ohlc;
    const intraday = marketSnapshot?.intraday;
    const indicatorData = technicalSnapshot?.indicator_data;
    const returns = technicalSnapshot?.return_data?.returns;

    const latestRSI =
      intraday?.indicators?.rsi?.length > 0
        ? intraday.indicators.rsi.at(-1)?.value
        : indicatorData?.indicators?.rsi?.at?.(-1)?.value;

    const latestMACD =
      intraday?.indicators?.macd?.length > 0
        ? intraday.indicators.macd.at(-1)?.macd
        : indicatorData?.indicators?.macd?.at?.(-1)?.macd;

    const targetPriceData = fundamentals?.targetPrice;
    const keyStats = fundamentals?.keyStatistics?.body;

    return {
      overview: {
        symbol,
        companyName: stockName?.company || stockName?.name || symbol,
        ltp: quote?.last_price,
        open: ohlc?.open,
        high: ohlc?.high,
        low: ohlc?.low,
        previousClose: ohlc?.close,
        percentChange: Number(percentChange),
        dayHigh: ohlc?.high,
        dayLow: ohlc?.low,
        week52High: quote?.["52_week_high"],
        week52Low: quote?.["52_week_low"],
        volume: quote?.volume,
      },

      forecast: {
        targetPrice: safeNumber(targetPriceData?.target_price),
        targetLow: safeNumber(targetPriceData?.target_low),
        targetHigh: safeNumber(targetPriceData?.target_high),
        upsidePercent: Number(projectChange || 0),
      },

      technicals: {
        rsi: latestRSI,
        macd: latestMACD,
        vwap: intraday?.vwap,
        vwapSignal: intraday?.vwap_signal,
        utSignal: intraday?.ut_signal,
        bbaweSignal: intraday?.bbawe_signal,
        rsiMacdSignal: intraday?.prediction,
        nearestResistance: nearestResistance?.value,
        nearestSupport: nearestSupport?.value,
        stopLoss: analysis?.stopLoss,
        traderNote: analysis?.traderNote,
        signal: analysis?.signal,
        exit: analysis?.exit,
      },

      fundamentals: {
        peRatio: keyStats?.forwardPE?.fmt || keyStats?.trailingPE?.fmt,
        marketCap: keyStats?.marketCap?.fmt,
        beta: keyStats?.beta?.fmt,
        bookValue: keyStats?.bookValue?.fmt,
        profitMargins: keyStats?.profitMargins?.fmt,
        returnOnEquity: keyStats?.returnOnEquity?.fmt,
        debtToEquity: keyStats?.debtToEquity?.fmt,
      },

      returns: {
        oneWeek: safeNumber(returns?.oneWeek),
        oneMonth: safeNumber(returns?.oneMonth),
        ytd: safeNumber(returns?.ytd),
        oneYear: safeNumber(returns?.oneYear),
        threeYear: safeNumber(returns?.threeYear),
        fiveYear: safeNumber(returns?.fiveYear),
      },

      marketDepth: {
        buyPercent,
        sellPercent,
        totalBuyQty,
        totalSellQty,
        buyOrders,
        sellOrders,
      },

      pivots: {
        timeframe: resistTimeFrame,
        classic: resistanceDataToRender?.classic,
        fibonacci: resistanceDataToRender?.fibonacci,
        camarilla: resistanceDataToRender?.camarilla,
        woodie: resistanceDataToRender?.woodie,
        demark: resistanceDataToRender?.demark,
      },

      movingAverages: {
        rows,
        summary,
      },

      meta: {
        generatedAt: new Date().toISOString(),
        timeframe: timeFrame,
        quoteUpdatedAt: marketSnapshot?.quote_updated_at,
        intradayUpdatedAt: marketSnapshot?.intraday_updated_at,
        source: "StockDetailPage",
      },
    };
  };

  const handleDownloadReport = async () => {
    try {
      if (!stockDetail || !marketSnapshot || !quote) {
        window.alert("Stock data not ready. Please refresh once.");
        return;
      }

      await downloadStockPDF(buildPDFPayload(), symbol);
      window.alert("✅ Report downloaded successfully!");
    } catch (err) {
      console.error("PDF generation failed", err);
      window.alert("❌ PDF generation failed");
    }
  };

  const intradaySmartMoney =
    marketSnapshot?.smart_money || marketSnapshot?.intraday?.smart_money;

  const eodSmartMoney =
    technicalSnapshot?.smart_money ||
    technicalSnapshot?.indicator_data?.smart_money;

  const activeSmartMoney =
    smartMode === "intraday" ? intradaySmartMoney : eodSmartMoney;
  const getSmartMoneyColor = (status) => {
    if (status === "Strong Accumulation" || status === "Accumulation") {
      return "#249966"; // same green as Bullish
    } else if (status === "Distribution" || status === "Heavy Distribution") {
      return "#dc2626"; // same red as Bearish
    } else {
      return "#fbbf24"; // same yellow as Neutral
    }
  };
  const getRSIMACD = (status) => {
    if (status === "Bullish" || status === "BUY") {
      return "#249966"; // same green as Bullish
    } else if (status === "Bearish" || status === "SELL") {
      return "#dc2626"; // same red as Bearish
    } else {
      return "#fbbf24"; // same yellow as Neutral
    }
  };
  //   const priceDiff =
  //     last_price && close ? (last_price - close).toFixed(2) : "0.00";
  //   console.log("News data", newsData);
  const scoreData = useMemo(() => {
    return getTechnicalScores({
      rsi: intraday?.indicators?.rsi?.at(-1)?.value,

      macd: intraday?.indicators?.macd?.at(-1)?.macd,

      signal: intraday?.indicators?.macd?.at(-1)?.signal,

      histogram: intraday?.indicators?.macd?.at(-1)?.histogram,

      ltp: ltp,

      vwap,

      smartMoneyScore: activeSmartMoney?.score || 50,

      prediction: rsi_macd_signal,

      utSignal: ut_signal,

      bbSignal: bb_signal === "BUY" ? "Bullish" : "Bearish",
    });
  }, [
    intraday,
    ltp,
    vwap,
    activeSmartMoney,
    rsi_macd_signal,
    ut_signal,
    bb_signal,
  ]);
  const ScoreMiniCard = ({ title, score, label, tooltip }) => {
    return (
      <div className="stockpro-score-mini-card">
        <div className="stockpro-score-circle">{score ?? "—"}</div>

        <div className="stockpro-score-content">
          <span>
            {title} <InfoTooltip text={tooltip} />
          </span>

          <h3>{label || "Neutral"}</h3>

          <p>{score ?? "—"}/100</p>
        </div>
      </div>
    );
  };
  const getUTBotScore = (utSignal) => {
    if (!utSignal) {
      return {
        score: 50,
        label: "Neutral",
      };
    }

    const signal = String(utSignal).toLowerCase();

    if (signal.includes("bullish")) {
      return {
        score: 80,
        label: "Bullish",
      };
    }

    if (signal.includes("bearish")) {
      return {
        score: 20,
        label: "Bearish",
      };
    }

    return {
      score: 50,
      label: "Neutral",
    };
  };
  const getBBScore = (bbSignal) => {
    if (!bbSignal) {
      return {
        score: 50,
        label: "Neutral",
      };
    }

    const signal = String(bbSignal).toLowerCase();

    if (signal === "buy" || signal === "bullish") {
      return {
        score: 80,
        label: "Bullish",
      };
    }

    if (signal === "sell" || signal === "bearish") {
      return {
        score: 20,
        label: "Bearish",
      };
    }

    return {
      score: 50,
      label: "Neutral",
    };
  };
  const getScoreTheme = (score) => {
    if (score >= 80) {
      return {
        bg: "#dcfce7",
        border: "#86efac",
        circle: "#16a34a",
        text: "#15803d",
      };
    }

    if (score >= 60) {
      return {
        bg: "#ecfdf5",
        border: "#bbf7d0",
        circle: "#22c55e",
        text: "#249966",
      };
    }

    if (score >= 45) {
      return {
        bg: "#fefce8",
        border: "#fde68a",
        circle: "#eab308",
        text: "#ca8a04",
      };
    }

    if (score >= 30) {
      return {
        bg: "#fef2f2",
        border: "#fecaca",
        circle: "#ef4444",
        text: "#dc2626",
      };
    }

    return {
      bg: "#fee2e2",
      border: "#fca5a5",
      circle: "#dc2626",
      text: "#991b1b",
    };
  };

  const getOverallSummary = ({
    overallScore,
    rsiScore,
    vwapScore,
    utScore,
    bbScore,
    smartScore,
  }) => {
    const bullishCount = [
      rsiScore >= 70,
      vwapScore >= 70,
      utScore >= 70,
      bbScore >= 70,
      smartScore >= 70,
    ].filter(Boolean).length;

    if (overallScore >= 80) {
      return {
        title: "Strong Bullish Setup",
        desc: `${bullishCount}/5 indicators are bullish. Momentum, trend, VWAP and smart money support upside continuation.`,
      };
    }

    if (overallScore >= 60) {
      return {
        title: "Bullish Setup",
        desc: `${bullishCount}/5 indicators support the trend. Buy on pullbacks is better than chasing.`,
      };
    }

    if (overallScore >= 40) {
      return {
        title: "Neutral Setup",
        desc: `${bullishCount}/5 indicators are bullish. Wait for better confirmation.`,
      };
    }

    if (overallScore >= 20) {
      return {
        title: "Bearish Setup",
        desc: "Weakness is visible. Avoid aggressive long positions.",
      };
    }

    return {
      title: "Strong Bearish Setup",
      desc: "Most indicators show downside pressure. Risk is high.",
    };
  };

  const rsiScore = scoreData?.rsiMacdScore ?? 50;
  const smartScore = activeSmartMoney?.score ?? 50;
  const vwapScore = scoreData?.vwapScore ?? 50;
  const utScore = getUTBotScore(ut_signal)?.score ?? 50;
  const bbScore = getBBScore(bb_signal)?.score ?? 50;
  const overallScore = scoreData?.overallScore ?? 50;
  const overallSummary = getOverallSummary({
    overallScore,
    rsiScore,
    vwapScore,
    utScore,
    bbScore,
    smartScore,
  });
  console.log("Running Analysis", analysis);
  const smart_money_index = indexDetail?.intraday?.smart_money;
  console.log("Smart moeney", smart_money_index);

  const rsiTheme = getScoreTheme(scoreData?.rsiMacdScore);

  const smartTheme = getScoreTheme(smart_money_index?.score);

  const vwapTheme = getScoreTheme(scoreData?.vwapScore);

  const utTheme = getScoreTheme(getUTBotScore(ut_signal)?.score);

  const bbTheme = getScoreTheme(getBBScore(bb_signal)?.score);

  const overallTheme = getScoreTheme(scoreData?.overallScore);

  const option_symbol = indexDetail?.option_symbol;
  const type = "INDEX";
  return (
    <>
      <div className="last-updated-head">
        {formattedQuoteTime && <span>As on {formattedQuoteTime}</span>}
        <FiRefreshCw
          onClick={handleRefresh}
          size={18}
          style={{
            cursor: "pointer",
            transition: "0.2s",
          }}
          onMouseOver={(e) => (e.target.style.color = "#2fd18a")}
          onMouseOut={(e) => (e.target.style.color = "#9ca3af")}
        />
      </div>

      {indexDetailLoading && (
        <p
          style={{
            fontSize: "1rem",
            fontWeight: "700",
            display: "flex",
            gap: "1rem",
          }}
        >
          Fetching Live data...
          <span style={{ paddingLeft: "10px", marginTop: "5px" }}>
            <FaSpinner />
          </span>
        </p>
      )}

      {indexDetailError && (
        <div
          style={{
            marginTop: "20px",
            padding: "14px",
            borderLeft: "5px solid #dc2626",
            background: "#fee2e2",
            color: "#7f1d1d",
            fontWeight: 600,
            borderRadius: "8px",
          }}
        >
          Currently data not available for {index_name}
        </div>
      )}

      <>
        {!indexDetailLoading && !indexDetailError && (
          <div className={`stockpro-page ${chatOpen ? "shrink" : ""}`}>
            <section className="stockpro-header">
              <div className="stockpro-price-title">
                <div className="stockpro-head-block">
                  <div className="stockpro-title-block">
                    <h1>{indexDetail?.index_name}</h1>
                    <p className="stockpro-symbol stock-tag">
                      {indexDetail?.index_name}
                    </p>
                  </div>

                  <div className="stockpro-price-block">
                    <h2>₹{ltp || "—"}</h2>

                    <p
                      className={
                        Number(percentChange) >= 0
                          ? "stockpro-green"
                          : "stockpro-red"
                      }
                    >
                      {Number(percentChange) >= 0 ? "▲" : "▼"} {priceDiff}
                      <span>
                        (
                        {percentChange > 0
                          ? `+${percentChange}`
                          : percentChange || "0.00"}
                        %)
                      </span>
                    </p>
                  </div>
                </div>

                <div className="stockpro-header-metrics">
                  <div className="stockpro-metric">
                    <span>
                      LTP
                      <InfoTooltip text={TOOLTIPS?.ltp} />
                    </span>
                    <b>₹{ltp || "—"}</b>
                  </div>

                  <div className="stockpro-metric">
                    <span>
                      Open <InfoTooltip text={TOOLTIPS?.open} />
                    </span>
                    <b>₹{open || "—"}</b>
                  </div>

                  <div className="stockpro-metric">
                    <span>
                      High <InfoTooltip text={TOOLTIPS?.high} />
                    </span>
                    <b>₹{high || "—"}</b>
                  </div>

                  <div className="stockpro-metric">
                    <span>
                      Low <InfoTooltip text={TOOLTIPS?.low} />
                    </span>
                    <b>₹{low || "—"}</b>
                  </div>

                  <div className="stockpro-metric">
                    <span>
                      Prev Close <InfoTooltip text={TOOLTIPS?.close} />
                    </span>
                    <b>₹{prevClose || "—"}</b>
                  </div>
                </div>
              </div>
            </section>

            {/* ================= SIGNAL STRIP ================= */}

            <section className="stockpro-signal-strip">
              <div className="stockpro-insight-heading">
                <div>
                  <h3>Quant Kubera Insights</h3>
                </div>
                <div className="stockpro-option-links">
                  <button
                    type="button"
                    className="stockpro-option-link"
                    onClick={() =>
                      navigate(`/optionpage/${type}/${option_symbol}`)
                    }
                  >
                    <span>
                      Option Chain <InfoTooltip text={TOOLTIPS?.optionChain} />
                    </span>
                  </button>

                  <button
                    type="button"
                    className="stockpro-option-link"
                    onClick={() =>
                      navigate(`/maxpainpage/${type}/${option_symbol}`)
                    }
                  >
                    <span>
                      Max Pain <InfoTooltip text={TOOLTIPS?.maxPain} />
                    </span>
                  </button>
                </div>
              </div>
              <div className="signal-strip-main">
                <div className="signal-strip-inner">
                  {[
                    {
                      label: "VWAP",
                      value: vwap_signal,
                      tooltip: TOOLTIPS?.vwapMomentum,
                    },

                    {
                      label: "RSI MACD",
                      value: rsi_macd_signal,
                      tooltip: TOOLTIPS?.rsiMacdSignal,
                    },

                    {
                      label: "UT Bot",
                      value: ut_signal,
                      tooltip: TOOLTIPS?.utBot,
                    },

                    {
                      label: "Bollinger Band",
                      value: bb_signal === "BUY" ? "Bullish" : "Bearish",
                      tooltip: TOOLTIPS?.bbSignal,
                    },

                    {
                      label: "Smart Money",
                      value: intraday?.smart_money?.signal,
                      tooltip: TOOLTIPS?.smartMoney,
                    },
                  ].map((item) => (
                    <div className="stockpro-signal-chip" key={item.label}>
                      <span>
                        {item.label} <InfoTooltip text={item?.tooltip} />
                      </span>
                      <b
                        className={
                          item.value === "BUY" ||
                          item.value === "Bullish" ||
                          item.value === "Accumulation" ||
                          item.value?.includes?.("Bullish")
                            ? "stockpro-bull"
                            : item.value === "SELL" ||
                                item.value === "Bearish" ||
                                item.value === "Distribution" ||
                                item.value?.includes?.("Bearish")
                              ? "stockpro-bear"
                              : "stockpro-neutral"
                        }
                      >
                        {item.value || "Neutral"}
                      </b>
                    </div>
                  ))}
                </div>

                <div className="stockpro-actions">
                  {/* <button onClick={handleRefresh}>Refresh</button> */}
                  <button
                    onClick={() => downloadStockPDF?.(buildPDFPayload?.())}
                  >
                    Download PDF ⬇
                  </button>
                  {/* <button
                    onClick={() =>
                      isActive
                        ? removeFromWatchlist(symbol)
                        : addToWatchlist({
                            symbol,
                            securityId: paramSecurityId,
                          })
                    }
                  >
                    {isActive ? "Remove Watchlist" : "Add Watchlist"}
                  </button> */}
                </div>
              </div>
            </section>

            {/* ================= MAIN GRID ================= */}
            <section className="stockpro-grid">
              <div className="stockpro-main">
                <div className="stockpro-two-col">
                  <section className="stockpro-card stockpro-score-section mobile-qk-score">
                    <div className="stockpro-card-head">
                      <h3>
                        QK Technical Score{" "}
                        <InfoTooltip text={TOOLTIPS?.technicalScore} />
                      </h3>
                    </div>

                    <section
                      className="stockpro-overall-hero"
                      style={{
                        "--card-bg": overallTheme.cardBg,
                        "--card-border": overallTheme.border,
                        "--score-color": overallTheme.score,
                        "--text-color": overallTheme.text,
                      }}
                    >
                      <div>
                        <OverallScoreGauge score={scoreData?.overallScore} />
                      </div>

                      <div className="overall-content">
                        <h2>{overallSummary?.title}</h2>

                        <p>{overallSummary?.desc}</p>

                        <div className="qk-score-tabs">
                          {[
                            {
                              label: "UT Bot",
                              score: utScore,
                              status: getUTBotScore(ut_signal)?.label,
                            },
                            {
                              label: "Bollinger Band",
                              score: bbScore,
                              status: getBBScore(bb_signal)?.label,
                            },
                            {
                              label: "VWAP",
                              score: vwapScore,
                              status: scoreData?.vwapLabel,
                            },
                            {
                              label: "Smart Money",
                              score: smart_money_index?.score,
                              status: smart_money_index?.status,
                            },
                            {
                              label: "RSI MACD",
                              score: rsiScore,
                              status: scoreData?.rsiMacdLabel,
                            },
                          ].map((item) => {
                            const theme = getScoreTheme(item.score);

                            return (
                              <div
                                key={item.label}
                                className="qk-score-tab"
                                style={{
                                  "--tab-bg": theme.bg,
                                  "--tab-border": theme.border,
                                  "--tab-color": theme.text,
                                }}
                              >
                                <b>{item.label}</b>
                                <small>{item.score}/100</small>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="score-range-guide">
                        <div>
                          <span className="range-dot strong-bull"></span> 80-100
                          Strong Bullish
                        </div>
                        <div>
                          <span className="range-dot bull"></span> 60-79 Bullish
                        </div>
                        <div>
                          <span className="range-dot neutral"></span> 40-59
                          Neutral
                        </div>
                        <div>
                          <span className="range-dot bear"></span> 20-39 Bearish
                        </div>
                        <div>
                          <span className="range-dot strong-bear"></span> 0-19
                          Strong Bearish
                        </div>
                      </div>
                    </section>
                  </section>
                  <section className="stockpro-card stockpro-tech-card mobile-technical">
                    <div className="stockpro-card-head">
                      <h3>Technical Snapshot</h3>
                      <span>{timeFrame === "min" ? "Intraday" : "Daily"}</span>
                    </div>

                    <div className="stockpro-tech-grid">
                      <div className="stockpro-tech-block">
                        <div className="stockpro-tech-block-head">
                          <h4>
                            Momentum & Exhaustion{" "}
                            <InfoTooltip text={TOOLTIPS?.rsiMacdSignal} />
                          </h4>

                          <div className="stockpro-tabs small">
                            <button
                              className={timeFrame === "min" ? "active" : ""}
                              onClick={() => setTimeFrame("min")}
                            >
                              Min
                            </button>
                            <button
                              className={timeFrame === "day" ? "active" : ""}
                              onClick={() => setTimeFrame("day")}
                            >
                              Day
                            </button>
                          </div>
                        </div>

                        <div
                          className="stockpro-smart-hero"
                          style={{
                            background: rsiTheme.bg,

                            border: `1px solid ${rsiTheme.border}`,
                          }}
                        >
                          <div
                            className="stockpro-smart-score"
                            style={{
                              borderColor: rsiTheme.circle,

                              color: rsiTheme.circle,
                            }}
                          >
                            {scoreData?.rsiMacdScore ?? "—"}
                          </div>

                          <div>
                            <span>
                              RSI & MACD Score
                              <InfoTooltip text={TOOLTIPS?.rsiMacdScore} />
                            </span>
                            <h3
                              style={{
                                color: getRSIMACD(
                                  timeFrame === "min" ? min_pred : day_pred,
                                ),
                              }}
                            >
                              {timeFrame === "min" ? min_pred : day_pred}
                            </h3>
                            <p>
                              {timeFrame === "min" ? min_result : day_result}
                            </p>
                          </div>
                        </div>

                        <div className="stockpro-mini-grid">
                          <div className="stockpro-metric">
                            <span>
                              RSI <InfoTooltip text={TOOLTIPS?.rsi} />
                            </span>
                            <b>
                              {" "}
                              {timeFrame === "min"
                                ? intraday?.indicators?.rsi
                                    ?.at?.(-1)
                                    ?.value?.toFixed?.(2) || "—"
                                : technicalSnapshot?.rsi?.toFixed?.(2) || "—"}
                            </b>
                          </div>

                          <div className="stockpro-metric">
                            <span>
                              MACD <InfoTooltip text={TOOLTIPS?.macd} />
                            </span>
                            <b>
                              {timeFrame === "min"
                                ? intraday?.indicators?.macd
                                    ?.at?.(-1)
                                    ?.macd?.toFixed?.(2) || "—"
                                : technicalSnapshot?.macd?.toFixed?.(2) || "—"}
                            </b>
                          </div>

                          <div className="stockpro-metric">
                            <span>
                              Volume <InfoTooltip text={TOOLTIPS?.volume} />
                            </span>
                            <b>{formatVolume(quote?.volume)}</b>
                          </div>
                        </div>
                      </div>

                      <div className="stockpro-tech-block smart">
                        <div className="stockpro-tech-block-head">
                          <h4>
                            Smart Money Flow{" "}
                            <InfoTooltip text={TOOLTIPS?.smartMoney} />
                          </h4>

                          <div className="stockpro-tabs small">
                            <button
                              className={
                                smartMode === "intraday" ? "active" : ""
                              }
                              onClick={() => setSmartMode("intraday")}
                            >
                              Intraday
                            </button>
                            <button
                              className={smartMode === "eod" ? "active" : ""}
                              onClick={() => setSmartMode("eod")}
                            >
                              EOD
                            </button>
                          </div>
                        </div>

                        <div
                          className="stockpro-smart-hero"
                          style={{
                            background: smartTheme?.bg,

                            border: `1px solid ${smartTheme.border}`,
                          }}
                        >
                          <div
                            className="stockpro-smart-score"
                            style={{
                              borderColor: smartTheme.circle,

                              color: smartTheme.circle,
                            }}
                          >
                            {smart_money_index?.score ?? "—"}
                          </div>

                          <div>
                            <span>
                              Smart Money Score
                              <InfoTooltip text={TOOLTIPS?.smartMoneyScore} />
                            </span>
                            <h3
                              style={{
                                color: getSmartMoneyColor(
                                  smart_money_index?.status,
                                ),
                              }}
                            >
                              {smart_money_index?.status || "Neutral"}
                            </h3>
                            <p>{smart_money_index?.conclusion}</p>
                          </div>
                        </div>

                        <div className="stockpro-mini-grid">
                          <div className="stockpro-metric">
                            <span>
                              CMF <InfoTooltip text={TOOLTIPS?.cmf} />
                            </span>
                            <b>{smart_money_index?.cmf?.value ?? "—"}</b>
                          </div>

                          <div className="stockpro-metric">
                            <span>
                              RVOL <InfoTooltip text={TOOLTIPS?.rvol} />
                            </span>
                            <b>
                              {smart_money_index?.rvol
                                ? `${smart_money_index?.rvol?.value}x`
                                : "—"}
                            </b>
                          </div>

                          <div className="stockpro-metric">
                            <span>
                              Score{" "}
                              <InfoTooltip text={TOOLTIPS?.smartMoneyScore} />
                            </span>
                            <b>{smart_money_index?.score ?? "—"}/100</b>
                          </div>
                        </div>
                      </div>

                      <section className="stockpro-card">
                        <div className="stockpro-card-head">
                          <h3>
                            Value Benchmark
                            <InfoTooltip text={TOOLTIPS?.vwapMomentum} />
                          </h3>
                          <span>
                            {formattedIntradayTime || "Live Snapshot"}
                          </span>
                        </div>
                        <div className="vwap-score-box">
                          <div className="stockpro-vwap-box">
                            <span>
                              {analysis?.entry?.label || "VWAP Signal"}
                            </span>
                            <h2>VWAP ₹{Number(vwap || 0).toFixed(2)}</h2>
                            <p>
                              {analysis?.traderNote ||
                                "Waiting for clean VWAP setup"}
                            </p>
                          </div>
                          <div
                            className="stockpro-smart-hero"
                            style={{
                              background: vwapTheme.bg,

                              border: `1px solid ${vwapTheme.border}`,
                            }}
                          >
                            <div
                              className="stockpro-smart-score"
                              style={{
                                borderColor: vwapTheme.circle,

                                color: vwapTheme.circle,
                              }}
                            >
                              {scoreData?.vwapScore ?? "—"}
                            </div>

                            <div>
                              <span>
                                Vwap Score
                                <InfoTooltip text={TOOLTIPS?.vwapScore} />
                              </span>
                              <h3
                                style={{
                                  color: getRSIMACD(vwap_signal),
                                }}
                              >
                                {vwap_signal}
                              </h3>
                            </div>
                          </div>
                        </div>

                        <div className="stockpro-mini-grid">
                          <div className="stockpro-metric">
                            <span>
                              LTP - VWAP{" "}
                              <InfoTooltip text={TOOLTIPS?.vwapMomentum} />
                            </span>
                            <b
                              className={
                                diff >= 0 ? "stockpro-green" : "stockpro-red"
                              }
                            >
                              {Number(diff > 0 ? `+${diff}` : diff).toFixed(2)}{" "}
                              ({percent > 0 ? `+${percent}` : percent || 0}%)
                            </b>
                          </div>

                          <div className="stockpro-metric">
                            <span>
                              Stop Loss{" "}
                              <InfoTooltip text={TOOLTIPS?.stopLoss} />
                            </span>
                            <b>₹{analysis?.stopLoss || "—"}</b>
                          </div>

                          <div className="stockpro-metric">
                            <span>
                              Target{" "}
                              <InfoTooltip text={TOOLTIPS?.vwapKeyLevels} />
                            </span>
                            <b>
                              ₹
                              {analysis?.targets?.primary?.value?.toFixed?.(
                                2,
                              ) || "—"}
                            </b>
                          </div>

                          <div className="stockpro-metric">
                            <span>
                              Exit <InfoTooltip text={TOOLTIPS?.summary} />
                            </span>
                            <b>{analysis?.exit || "—"}</b>
                          </div>
                        </div>
                      </section>
                      <section className="stockpro-card">
                        <div className="stockpro-tech-block-head">
                          <h4>
                            Trend & Volatility Execution
                            <InfoTooltip text={TOOLTIPS?.rsiMacdSignal} />
                          </h4>
                        </div>

                        <div className="par-smart-hero">
                          <div
                            className="stockpro-smart-hero"
                            style={{
                              background: utTheme.bg,

                              border: `1px solid ${utTheme.border}`,
                            }}
                          >
                            <div
                              className="stockpro-smart-score"
                              style={{
                                borderColor: utTheme.circle,

                                color: utTheme.circle,
                              }}
                            >
                              {getUTBotScore(ut_signal)?.score ?? "—"}
                            </div>

                            <div>
                              <span>
                                UT Bot Score
                                <InfoTooltip text={TOOLTIPS?.utBotScore} />
                              </span>
                              <h3
                                style={{
                                  color: getRSIMACD(ut_signal),
                                }}
                              >
                                {ut_signal}
                              </h3>
                            </div>
                          </div>

                          <div
                            className="stockpro-smart-hero"
                            style={{
                              background: bbTheme.bg,

                              border: `1px solid ${bbTheme.border}`,
                            }}
                          >
                            <div
                              className="stockpro-smart-score"
                              style={{
                                borderColor: bbTheme.circle,

                                color: bbTheme.circle,
                              }}
                            >
                              {getBBScore(bb_signal)?.score ?? "—"}
                            </div>

                            <div>
                              <span>
                                BB Signal Score
                                <InfoTooltip text={TOOLTIPS?.bbSignalScore} />
                              </span>
                              <h3
                                style={{
                                  color: getRSIMACD(bb_signal),
                                }}
                              >
                                {getBBScore(bb_signal)?.label}
                              </h3>
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                  </section>
                </div>

                {/* ================= PIVOT TABLE ================= */}
                <section className="stockpro-card mobile-pivots">
                  <div className="stockpro-card-head">
                    <h3>
                      Pivot Levels <InfoTooltip text={TOOLTIPS?.pivotLevels} />
                    </h3>
                    <div className="stockpro-tabs">
                      <button
                        className={resistTimeFrame === "day" ? "active" : ""}
                        onClick={() => setResistTimeFrame("day")}
                      >
                        Daily
                      </button>
                      <button
                        className={resistTimeFrame === "week" ? "active" : ""}
                        onClick={() => setResistTimeFrame("week")}
                      >
                        Weekly
                      </button>
                    </div>
                  </div>

                  <div className="stockpro-table-wrap">
                    <table className="stockpro-table">
                      <thead>
                        <tr>
                          <th>Level</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Classic</th>
                          <th>Fibonacci</th>
                          <th>Camarilla</th>
                          <th>Woodie</th>
                          <th>DM</th>
                        </tr>
                      </thead>

                      <tbody>
                        {levels?.map((level) => {
                          const classic =
                            resistanceDataToRender?.classic?.[level.key];
                          const fibonacci =
                            resistanceDataToRender?.fib?.[level.key];
                          const camarilla =
                            resistanceDataToRender?.cam?.[level.key];
                          const woodie =
                            resistanceDataToRender?.woodie?.[level.key];
                          const demark =
                            resistanceDataToRender?.demark?.[level.key];

                          return (
                            <tr
                              key={level.key}
                              className={
                                level.key === "P" ? "stockpro-pivot-row" : ""
                              }
                            >
                              <td>
                                <b>{level.key}</b> - {level.label}
                              </td>
                              <td>{renderStars(getPriority(classic, ltp))}</td>
                              <td>
                                <span
                                  className={
                                    classic > ltp
                                      ? "stockpro-red"
                                      : "stockpro-green"
                                  }
                                >
                                  {getStatus(classic, ltp)}
                                </span>
                              </td>
                              <td>{classic?.toFixed?.(2) || "—"}</td>
                              <td>{fibonacci?.toFixed?.(2) || "—"}</td>
                              <td>{camarilla?.toFixed?.(2) || "—"}</td>
                              <td>{woodie?.toFixed?.(2) || "—"}</td>
                              <td>{demark?.toFixed?.(2) || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
                {/* ================= GANN LEVELS ================= */}
                {gannLevels && (
                  <section className="stockpro-card mobile-gann">
                    <div className="stockpro-card-head">
                      <h3>Gann Levels</h3>
                      <span>
                        Square of 9 · {gannLevels.degree}° · LTP ₹
                        {Number.isFinite(gannLtp) ? gannLtp.toFixed(2) : "—"}
                      </span>
                    </div>

                    <div className="stockpro-table-wrap">
                      <table className="stockpro-table">
                        <thead>
                          <tr>
                            <th>Level</th>
                            <th>Type</th>
                            <th>Price</th>
                            <th>Distance</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {GANN_ROWS.map((row) => {
                            const price = gannLevels[row.key];
                            const hasLtp =
                              Number.isFinite(gannLtp) && gannLtp > 0;
                            const pts = hasLtp ? price - gannLtp : null;
                            const pct = hasLtp ? (pts / gannLtp) * 100 : null;
                            const status = getGannStatus(
                              row.key,
                              price,
                              gannLtp,
                            );

                            return (
                              <tr
                                key={row.key}
                                className={
                                  row.key === gannNearestKey
                                    ? "stockpro-pivot-row"
                                    : ""
                                }
                              >
                                <td>
                                  <b
                                    className={
                                      row.key.startsWith("r")
                                        ? "stockpro-green"
                                        : row.key.startsWith("s")
                                          ? "stockpro-red"
                                          : ""
                                    }
                                  >
                                    {row.label}
                                  </b>
                                </td>
                                <td>{row.type}</td>
                                <td>₹{price.toFixed(2)}</td>
                                <td>
                                  {pts == null
                                    ? "—"
                                    : `${pts > 0 ? "+" : ""}${pts.toFixed(2)} (${pct > 0 ? "+" : ""}${pct.toFixed(2)}%)`}
                                </td>
                                <td>
                                  <span className={status.cls}>
                                    {status.text}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                <section className="stockpro-card mobile-return">
                  <div className="stockpro-card-head">
                    <h3>
                      Return Performance{" "}
                      <InfoTooltip text={TOOLTIPS?.returnPerformance} />
                    </h3>
                  </div>

                  <div className="stockpro-return-grid">
                    {[
                      {
                        label: "1W",
                        value: oneWeekReturn,
                        tooltip: TOOLTIPS?.oneWeekReturn,
                      },
                      {
                        label: "1M",
                        value: oneMonthReturn,
                        tooltip: TOOLTIPS?.oneMonthReturn,
                      },
                      {
                        label: "YTD",
                        value: ytdReturn,
                        tooltip: TOOLTIPS?.ytdReturn,
                      },
                      {
                        label: "1Y",
                        value: oneYearReturn,
                        tooltip: TOOLTIPS?.oneYearReturn,
                      },
                      {
                        label: "3Y",
                        value: threeYearReturn,
                        tooltip: TOOLTIPS?.threeYearReturn,
                      },
                      {
                        label: "5Y",
                        value: fiveYearReturn,
                        tooltip: TOOLTIPS?.fiveYearReturn,
                      },
                    ].map((item) => (
                      <div className="stockpro-return-item" key={item.label}>
                        <span>
                          {item.label} <InfoTooltip text={item?.tooltip} />
                        </span>
                        <b
                          className={
                            Number(item.value) >= 0
                              ? "stockpro-green"
                              : "stockpro-red"
                          }
                        >
                          {item?.value > 0
                            ? `+${item?.value}`
                            : (item?.value ?? "-")}
                        </b>
                      </div>
                    ))}
                  </div>
                </section>

                {/* ================= MA + RETURNS ================= */}

                <section className="stockpro-card mobile-moving-average">
                  <div className="stockpro-card-head">
                    <h3>
                      Moving Averages{" "}
                      <InfoTooltip text={TOOLTIPS?.movingAverage} />
                    </h3>
                    <span>
                      Buy {summary?.finalBuy || 0} / Sell{" "}
                      {summary?.finalSell || 0}
                    </span>
                  </div>

                  <div className="stockpro-table-wrap">
                    <Gauge buy={summary?.finalBuy} sell={summary?.finalSell} />
                    <div className="mobile-ma-table-scroll">
                      <table className="stockpro-table">
                        <thead>
                          <tr>
                            <th>Period</th>
                            <th>SMA</th>
                            <th>SMA Signal</th>
                            <th>EMA</th>
                            <th>EMA Signal</th>
                          </tr>
                        </thead>

                        <tbody>
                          {rows?.map((row) => (
                            <tr key={row.name}>
                              <td>{row.name}</td>
                              <td>{row.smaValue?.toFixed?.(2)}</td>
                              <td>
                                <span
                                  className={
                                    row.smaTrend === "Buy"
                                      ? "stockpro-green"
                                      : "stockpro-red"
                                  }
                                >
                                  {row.smaTrend}
                                </span>
                              </td>
                              <td>{row.emaValue?.toFixed?.(2)}</td>
                              <td>
                                <span
                                  className={
                                    row.emaTrend === "Buy"
                                      ? "stockpro-green"
                                      : "stockpro-red"
                                  }
                                >
                                  {row.emaTrend}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>

                {/* Crossover */}
                <section className="stockpro-card mobile-macd-crossover">
                  <div className="stockpro-card-head">
                    <h3>
                      MACD Crossover History{" "}
                      <InfoTooltip text={TOOLTIPS?.macdCrossover} />
                    </h3>
                    <span>{timeFrame === "min" ? "Minute" : "Daily"}</span>
                  </div>

                  <div className="stockpro-table-wrap">
                    <table className="stockpro-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Price</th>
                          <th>MACD</th>
                          <th>Signal</th>
                          <th>Message</th>
                        </tr>
                      </thead>

                      <tbody>
                        {crossovers
                          ?.slice?.()
                          .reverse?.()
                          .slice(0, visibleCount)
                          ?.map((item, index) => (
                            <tr key={`${item.date}-${index}`}>
                              <td>{item.date}</td>
                              <td>₹{item.price?.toFixed?.(2) || "—"}</td>
                              <td>{item.macd?.toFixed?.(2) || "—"}</td>
                              <td>{item.signal?.toFixed?.(2) || "—"}</td>
                              <td>
                                {item.type === "BUY"
                                  ? "Bullish crossover detected"
                                  : "Bearish crossover detected"}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {crossovers?.length > visibleCount && (
                    <button
                      className="stockpro-load-btn"
                      onClick={() => setVisibleCount((prev) => prev + 8)}
                    >
                      View More
                    </button>
                  )}
                </section>
                {/* Chart */}
                {/* {macdChartData && macdChartData.length > 0 && ( */}
                <div className="stockpro-chart-wrapper mobile-charts">
                  <div className="macd-chart-graph">
                    <div className="chart-top-heading">
                      <h3>
                        Price, MACD Line, Signal{" "}
                        <InfoTooltip text={TOOLTIPS?.signalLine} /> & Histogram{" "}
                        <InfoTooltip text={TOOLTIPS?.histogram} />
                      </h3>
                      <div className="chart-select">
                        <label>Interval</label>
                        <div style={{ display: "flex", gap: "10px" }}>
                          <select
                            name=""
                            id=""
                            value={timeFrame}
                            onChange={(e) => setTimeFrame(e.target.value)}
                            style={{
                              background: "inherit",

                              fontWeight: "600",
                              color: "black",

                              boxShadow: " 0 4px 12px rgba(0, 0, 0, 0.4)",
                              padding: "5px 7px",
                              borderRadius: "10px",
                            }}
                          >
                            <option value="min">Minute</option>
                            <option value="day">Day</option>
                          </select>
                          <select
                            value={interval}
                            onChange={(e) => setInterval(e.target.value)}
                            style={{
                              background: "inherit",
                              fontWeight: "600",
                              color: "black",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                              padding: "5px 7px",
                              borderRadius: "10px",
                            }}
                          >
                            {timeFrame === "min" && (
                              <>
                                <option value="1m">1 Minute</option>
                                <option value="3m">3 Minutes</option>
                                <option value="5m">5 Minutes</option>
                              </>
                            )}

                            {timeFrame === "day" && (
                              <>
                                <option value="1d">1 Day</option>
                                <option value="1w">1 Week</option>
                                <option value="1mo">1 Month</option>
                                <option value="3mo">3 Months</option>
                              </>
                            )}
                          </select>
                        </div>
                      </div>
                    </div>
                    <section
                      className={`stockpro-card ${
                        maxChart === "macdPrice" ? "chart-max-view" : ""
                      }`}
                    >
                      <div
                        className="stockpro-card-head"
                        style={{
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          className="chart-max-btn"
                          onClick={() =>
                            setMaxChart(
                              maxChart === "macdPrice" ? null : "macdPrice",
                            )
                          }
                        >
                          {maxChart === "macdPrice" ? "Minimize" : "Maximize"}
                        </button>
                      </div>

                      <div className="stockpro-chart-section">
                        <h3 className="trend-head">
                          Macd Trend <InfoTooltip text={TOOLTIPS?.macdLine} />
                        </h3>
                        <div className="stockpro-chart-card">
                          <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart
                              data={selectedData}
                              syncId="stock-sync"
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#374151"
                              />

                              <XAxis
                                dataKey="date"
                                tickFormatter={(date, index) => {
                                  if (!date) return "";

                                  if (timeFrame === "min") {
                                    if (typeof date === "string") {
                                      const timePart = date
                                        ?.split(",")[1]
                                        ?.trim(); // "12:35:00 pm"
                                      if (!timePart) return "";

                                      const [hhmm] = timePart.split(":"); // "12"
                                      const minute = timePart.split(":")[1]; // "35"

                                      return index % 10 === 0
                                        ? `${hhmm}:${minute}`
                                        : "";
                                    }
                                    return "";
                                  }
                                  const d = new Date(date);
                                  if (isNaN(d.getTime())) return "";
                                  return index % 15 === 0
                                    ? d?.toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                      })
                                    : "";
                                }}
                                tick={{ fontWeight: "bold", fontSize: 10 }}
                                axisLine={{ stroke: "#fff" }}
                                tickLine={{ stroke: "#fff" }}
                              />

                              <YAxis
                                tick={{
                                  fontWeight: "bold",
                                  fontSize: 10,
                                }}
                                axisLine={{ stroke: "#fff" }}
                                tickLine={{ stroke: "#fff" }}
                              />

                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "#1f2937",
                                  border: "none",
                                  color: "#fff",
                                }}
                                labelFormatter={(label) => {
                                  try {
                                    if (!label) return "";
                                    const hasTime = label.includes(":");
                                    const d = new Date(label.replace(" ", "T"));
                                    if (isNaN(d.getTime())) return label;

                                    if (hasTime) {
                                      return d.toLocaleString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                      });
                                    } else {
                                      return d.toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      });
                                    }
                                  } catch (err) {
                                    return label;
                                  }
                                }}
                                formatter={(value, name) => {
                                  if (value == null) return ["–", name];

                                  return [
                                    Number(value).toFixed(2), // 🔥 FIX DECIMALS
                                    name === "MACD"
                                      ? "MACD"
                                      : name === "signal"
                                        ? "Signal"
                                        : "Histogram",
                                  ];
                                }}
                                labelStyle={{
                                  color: "#fff",
                                  fontWeight: "bold",
                                }}
                                itemStyle={{ color: "#fff" }}
                              />

                              <Legend
                                wrapperStyle={{ fontWeight: "bold" }}
                                payload={[
                                  {
                                    value: "Histogram (Bullish)", // custom label
                                    type: "square",
                                    color: "#2fd18a", // green for positive
                                  },
                                  {
                                    value: "Histogram (Bearish)",
                                    type: "square",
                                    color: "#f87171", // red for negative
                                  },
                                  {
                                    value: "MACD",
                                    type: "line",
                                    color: "#2563eb", // blue line
                                  },
                                  {
                                    value: "Signal",
                                    type: "line",
                                    color: "#f59e0b", // orange line
                                  },
                                ]}
                              />

                              <Bar
                                dataKey="histogram"
                                name="Histogram"
                                barSize={20}
                                shape={(props) => {
                                  const { x, y, width, height, value } = props;

                                  const barHeight = Math.abs(height);

                                  return (
                                    <rect
                                      x={x}
                                      y={value >= 0 ? y : y - barHeight} // 🔥 key fix
                                      width={width}
                                      height={barHeight} // 🔥 absolute height
                                      fill={value >= 0 ? "#2fd18a" : "#f87171"}
                                    />
                                  );
                                }}
                              />

                              <Line
                                type="monotone"
                                dataKey="MACD"
                                stroke="#2563eb"
                                strokeWidth={2}
                                dot={false}
                                name="MACD"
                              />

                              <Line
                                type="monotone"
                                dataKey="signal"
                                stroke="#f59e0b"
                                strokeWidth={2}
                                dot={false}
                                name="Signal"
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="stockpro-chart-section">
                        <h3 className="trend-head">
                          Price Trend{" "}
                          <InfoTooltip text={TOOLTIPS?.priceChart} />
                        </h3>
                        <div className="stockpro-chart-card">
                          <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart
                              data={selectedPriceData}
                              syncId="stock-sync" // 🔥 LINKS WITH MACD & RSI
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#374151"
                              />

                              <XAxis
                                dataKey="date"
                                tickFormatter={(date, index) => {
                                  if (!date) return "";

                                  if (
                                    timeFrame === "min" &&
                                    typeof date === "string"
                                  ) {
                                    const timePart = date.split(",")[1]?.trim(); // "12:35:00 pm"
                                    if (!timePart) return "";

                                    const [hh, mm] = timePart.split(":");
                                    return index % 10 === 0
                                      ? `${hh}:${mm}`
                                      : "";
                                  }

                                  const d = new Date(date);
                                  return index % 15 === 0
                                    ? d.toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                      })
                                    : "";
                                }}
                                tick={{ fontWeight: "bold", fontSize: 10 }}
                                axisLine={{ stroke: "#fff" }}
                                tickLine={{ stroke: "#fff" }}
                              />

                              <YAxis
                                tick={{ fontWeight: "bold", fontSize: 10 }}
                                axisLine={{ stroke: "#fff" }}
                                tickLine={{ stroke: "#fff" }}
                                domain={["auto", "auto"]}
                              />

                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "#1f2937",
                                  border: "none",
                                  color: "#fff",
                                }}
                                labelFormatter={(label) => {
                                  if (!label) return "";

                                  const hasTime = label.includes(":");
                                  const d = new Date(label.replace(" ", "T"));

                                  if (isNaN(d.getTime())) return label;

                                  return hasTime
                                    ? d.toLocaleString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                      })
                                    : d.toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      });
                                }}
                                formatter={(value) => [
                                  Number(value).toFixed(2), // value
                                  "Price", // 🔥 rename Close Price → Price
                                ]}
                                labelStyle={{
                                  color: "#fff",
                                  fontWeight: "bold",
                                }}
                                itemStyle={{ color: "#fff" }}
                              />
                              <Legend
                                wrapperStyle={{ fontWeight: "bold" }}
                                payload={[
                                  {
                                    value: "Price",
                                    type: "line",
                                    color: "#22c55e", // same green as price line
                                  },
                                ]}
                              />

                              <Line
                                type="monotone"
                                dataKey="close"
                                stroke="#22c55e"
                                strokeWidth={2}
                                dot={false}
                                name="Close Price"
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </section>

                    <div className="stockpro-chart-section">
                      <div className="candle-chart-container">
                        <div className="chart-legend">
                          <h3 className="trend-head">
                            Candles Trend{" "}
                            <InfoTooltip text={TOOLTIPS?.candlestickChart} />
                          </h3>
                          <div className="candles-color-map">
                            <span className="legend-item vwap">🟡 VWAP</span>
                            <span className="legend-item buy">
                              🟢 BUY Signal
                            </span>
                            <span className="legend-item sell">
                              🔴 SELL Signal
                            </span>
                          </div>
                        </div>
                        <section
                          className={`stockpro-card ${maxChart === "candle" ? "chart-max-view" : ""}`}
                        >
                          <div className="stockpro-card-head">
                            <h3>Candle Chart</h3>

                            <button
                              className="chart-max-btn"
                              onClick={() =>
                                setMaxChart(
                                  maxChart === "candle" ? null : "candle",
                                )
                              }
                            >
                              {maxChart === "candle" ? "Minimize" : "Maximize"}
                            </button>
                          </div>

                          <div className="chart-body">
                            <CandleChart
                              intradayData={intradayVwap}
                              histData={indicatorData}
                              setLastUTSignal={setLastUTSignal}
                              chartIndicatorData={chartIndicatorData}
                            />
                          </div>
                        </section>
                      </div>
                    </div>
                  </div>
                </div>
                {/* )} */}
              </div>

              {/* ================= SIDEBAR ================= */}
              <aside className="stockpro-side">
                <section className="stockpro-card mobile-day-range">
                  <div className="stockpro-card-head">
                    <h3>
                      Day Range <InfoTooltip text={TOOLTIPS?.dayRange} />
                    </h3>
                  </div>

                  <div className="stockpro-range-box">
                    <div className="stockpro-range-labels">
                      <span>₹{low || "—"}</span>
                      <span>₹{high || "—"}</span>
                    </div>

                    <div className="stockpro-range-track">
                      <div
                        className="stockpro-range-dot"
                        style={{
                          left:
                            low && high && ltp
                              ? `${Math.min(
                                  100,
                                  Math.max(
                                    0,
                                    ((ltp - low) / (high - low)) * 100,
                                  ),
                                )}%`
                              : "50%",
                        }}
                      />
                    </div>

                    <p>LTP: ₹{ltp || "—"}</p>
                  </div>
                </section>

                {/* <section className="stockpro-card mobile-week52">
                  <div className="stockpro-card-head">
                    <h3>
                      52 Week Range <InfoTooltip text={TOOLTIPS?.week52Range} />
                    </h3>
                  </div>

                  <div className="stockpro-range-box">
                    <div className="stockpro-range-labels">
                      <span>₹{weekLow || "—"}</span>
                      <span>₹{weekHigh || "—"}</span>
                    </div>

                    <div className="stockpro-range-track">
                      <div
                        className="stockpro-range-dot"
                        style={{
                          left:
                            weekLow && weekHigh && ltp
                              ? `${Math.min(
                                  100,
                                  Math.max(
                                    0,
                                    ((ltp - weekLow) / (weekHigh - weekLow)) *
                                      100,
                                  ),
                                )}%`
                              : "50%",
                        }}
                      />
                    </div>

                    <p>LTP: ₹{ltp || "—"}</p>
                  </div>
                </section> */}

                {/* <section className="stockpro-card mobile-key-levels">
                  <div className="stockpro-card-head">
                    <h3>
                      Key Levels <InfoTooltip text={TOOLTIPS?.vwapKeyLevels} />
                    </h3>
                  </div>

                  <div className="stockpro-level-list">
                    <div>
                      <span>Nearest Resistance </span>
                      <b className="stockpro-red">
                        {nearestResistance?.value
                          ? ` ₹${nearestResistance?.value.toFixed(2)}`
                          : "All Resistance broken"}
                      </b>
                    </div>

                    <div>
                      <span>Nearest Support</span>
                      <b className="stockpro-green">
                        
                        {nearestSupport?.value
                          ? ` ₹${nearestSupport?.value.toFixed(2)}`
                          : "All Support broken"}
                      </b>
                    </div>

                    <div>
                      <span>VWAP</span>
                      <b>₹{Number(vwap || 0).toFixed(2)}</b>
                    </div>

                    <div>
                      <span>Stop Loss</span>
                      <b>₹{analysis?.stopLoss || "—"}</b>
                    </div>
                  </div>
                </section> */}

                {/* <section className="stockpro-card mobile-order-book">
                  <div className="stockpro-card-head">
                    <h3>
                      Order Book <InfoTooltip text={TOOLTIPS?.orderBook} />
                    </h3>
                  </div>

                  <div className="stockpro-order-progress">
                    <div style={{ width: `${buyPercent || 0}%` }} />
                  </div>

                  <div className="stockpro-order-summary">
                    <span className="stockpro-green">
                      Buy {buyPercent?.toFixed?.(2) || 0}%
                    </span>
                    <span className="stockpro-red">
                      Sell {sellPercent?.toFixed?.(2) || 0}%
                    </span>
                  </div>

                  <div className="stockpro-depth-table">
                    <div className="stockpro-depth-head">
                      <span>Buy Qty</span>
                      <span>Price</span>
                      <span>Sell Qty</span>
                    </div>

                    {[0, 1, 2, 3, 4].map((i) => (
                      <div className="stockpro-depth-row" key={i}>
                        <span className="stockpro-green">
                          {depth?.buy?.[i]?.quantity || "—"}
                        </span>
                        <span>
                          ₹
                          {depth?.buy?.[i]?.price ||
                            depth?.sell?.[i]?.price ||
                            "—"}
                        </span>
                        <span className="stockpro-red">
                          {depth?.sell?.[i]?.quantity || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section> */}

                {/* <section className="stockpro-card mobile-valuation">
                  <div className="stockpro-card-head">
                    <h3>
                      Valuation Measures{" "}
                      <InfoTooltip text={TOOLTIPS?.valuationMeasures} />
                    </h3>
                  </div>

                  <div className="stockpro-level-list">
                    <div>
                      <span>
                        Enterprise Value{" "}
                        <InfoTooltip text={TOOLTIPS?.enterpriseValue} />
                      </span>
                      <b>{statisticsData?.enterpriseValue?.fmt || "-"}</b>
                    </div>

                    <div>
                      <span>
                        Forward P/E <InfoTooltip text={TOOLTIPS?.forwardPE} />
                      </span>
                      <b>{statisticsData?.forwardPE?.fmt || "-"}</b>
                    </div>

                    <div>
                      <span>
                        Price to Book{" "}
                        <InfoTooltip text={TOOLTIPS?.priceToBook} />
                      </span>
                      <b>{statisticsData?.priceToBook?.fmt || "-"}</b>
                    </div>

                    <div>
                      <span>
                        Enterprise to Revenue{" "}
                        <InfoTooltip text={TOOLTIPS?.enterpriseToRevenue} />
                      </span>
                      <b>{statisticsData?.enterpriseToRevenue?.fmt || "-"}</b>
                    </div>
                  </div>
                </section> */}
                {/* <section className="stockpro-card mobile-fundamentals">
                  <div className="stockpro-card-head">
                    <h3>
                      Fundamental Factors{" "}
                      <InfoTooltip text={TOOLTIPS?.fundamentalFactors} />
                    </h3>
                  </div>

                  <div className="stockpro-level-list">
                    <div>
                      <span>
                        % Upside <InfoTooltip text={TOOLTIPS?.upside} />
                      </span>
                      <b
                        className="positive"
                        style={{
                          color: projectChange > 0 ? "#249966" : "#f87171",
                        }}
                      >
                        {" "}
                        {projectChange}%
                      </b>
                    </div>
                    <div>
                      <span>
                        Forecast Price{" "}
                        <InfoTooltip text={TOOLTIPS?.forecastPrice} />
                      </span>
                      <b>₹{target_price}</b>
                    </div>
                    <div>
                      <span>
                        P/E Ratio <InfoTooltip text={TOOLTIPS?.peRatio} />
                      </span>
                      <b>{peRatio}</b>
                    </div>
                  </div>
                </section> */}

                {/* <section className="stockpro-card mobile-profit">
                  <div className="stockpro-card-head">
                    <h3>
                      Profitability & Growth{" "}
                      <InfoTooltip text={TOOLTIPS?.profitability} />
                    </h3>
                  </div>

                  <div className="stockpro-level-list">
                    <div>
                      <span>
                        Profit Margin{" "}
                        <InfoTooltip text={TOOLTIPS?.profitMargin} />
                      </span>
                      <b>{statisticsData?.profitMargins?.fmt || "-"}</b>
                    </div>

                    <div>
                      <span>
                        Earnings Growth (QoQ){" "}
                        <InfoTooltip text={TOOLTIPS?.earningsGrowth} />
                      </span>
                      <b>
                        {statisticsData?.earningsQuarterlyGrowth?.fmt || "-"}
                      </b>
                    </div>

                    <div>
                      <span>
                        Trailing EPS{" "}
                        <InfoTooltip text={TOOLTIPS?.trailingEps} />
                      </span>
                      <b>{statisticsData?.trailingEps?.fmt}</b>
                    </div>
                    <div>
                      <span>
                        Forward EPS <InfoTooltip text={TOOLTIPS?.forwardEps} />
                      </span>
                      <b>{statisticsData?.forwardEps?.fmt}</b>
                    </div>
                  </div>
                </section> */}
                {/* <section className="stockpro-card mobile-ownership">
                  <div className="stockpro-card-head">
                    <h3>
                      Ownership & Risk{" "}
                      <InfoTooltip text={TOOLTIPS?.ownershipRisk} />
                    </h3>
                  </div>

                  <div className="stockpro-level-list">
                    <div>
                      <span>
                        Insider Holding
                        <InfoTooltip text={TOOLTIPS?.insiderHolding} />
                      </span>
                      <b>{statisticsData?.heldPercentInsiders?.fmt || "—"}</b>
                    </div>

                    <div>
                      <span>
                        Institution Holding{" "}
                        <InfoTooltip text={TOOLTIPS?.institutionalHolding} />
                      </span>
                      <b>
                        {statisticsData?.heldPercentInstitutions?.fmt || "—"}
                      </b>
                    </div>

                    <div>
                      <span>
                        Float Shares{" "}
                        <InfoTooltip text={TOOLTIPS?.floatShares} />
                      </span>
                      <b>{statisticsData?.floatShares?.fmt || "—"}</b>
                    </div>
                    <div>
                      <span>
                        Shares Outstanding
                        <InfoTooltip text={TOOLTIPS?.sharesOutstanding} />
                      </span>
                      <b>{statisticsData?.sharesOutstanding?.fmt}</b>
                    </div>
                  </div>
                </section> */}

                {/* {newsData?.body?.length > 0 && (
                  <section className="stockpro-card mobile-news">
                    <div className="stockpro-card-head">
                      <h3>News Insights</h3>
                      <span>Latest</span>
                    </div>

                    <div className="stockpro-news-list">
                      {newsData?.body?.length ? (
                        newsData?.body?.slice(0, 5).map((news, index) => (
                          <a
                            key={index}
                            href={news.link} // ✅ FIX
                            target="_blank"
                            rel="noopener noreferrer"
                            // className="news-card"
                          >
                            <div className="stockpro-news-item" key={index}>
                              <h4>{news?.title || news?.headline}</h4>
                            </div>
                          </a>
                        ))
                      ) : (
                        <p className="stockpro-empty">
                          No recent news available.
                        </p>
                      )}
                    </div>
                  </section>
                )} */}
              </aside>
            </section>
          </div>
        )}
      </>

      {/* <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      /> */}
    </>
  );
};
export default IndexDetail;
